import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CalendarClock,
  GripVertical,
  Pause,
  Pencil,
  Play,
  Radio,
} from "lucide-react";
import React from "react";
import { FaApple } from "react-icons/fa";
import { FcAndroidOs } from "react-icons/fc";
import type { HappeningNowListItem } from "src/types/happeningNow";
import { ClickBadge } from "./ClickBadge";

interface LiveCarouselCardProps {
  item: HappeningNowListItem;
  busy: boolean;
  /** True when the corresponding draft card differs from this live row. */
  hasDraftEdit?: boolean;
  readOnly?: boolean;
  onEditInDraft: (item: HappeningNowListItem) => void;
  onToggleActive: (item: HappeningNowListItem) => void;
}

const statusStyles = {
  active:
    "border-[#58CC02] bg-[#D7FFB8] text-[#2B7A0B] shadow-[0_2px_0_#58A700]",
  paused:
    "border-[#E5E5E5] bg-[#F7F7F7] text-[#777777] shadow-[0_2px_0_#D0D0D0]",
};

const actionButtonBase =
  "inline-flex h-10 w-10 items-center justify-center rounded-2xl border-2 text-sm transition-all active:translate-y-1 active:shadow-none disabled:pointer-events-none disabled:opacity-50";

const editButton =
  "border-[#E5E5E5] bg-white text-[#3C3C3C] shadow-[0_4px_0_#D0D0D0] hover:bg-[#F7F7F7]";

const pauseButton =
  "border-[#FF9600] bg-[#FFE7B8] text-[#A45A00] shadow-[0_4px_0_#E07B00] hover:bg-[#FFDFA1]";

const resumeButton =
  "border-[#58CC02] bg-[#D7FFB8] text-[#2B7A0B] shadow-[0_4px_0_#58A700] hover:bg-[#C8F8A4]";

/**
 * A row in the "Live carousel" section. Visually similar to the compact draft
 * card but with a distinct color band on the left so the operator can tell at
 * a glance which list they're looking at. Reorder is driven by `@dnd-kit` —
 * the parent wraps the list in a `SortableContext` and we expose
 * `GripVertical` as the drag handle.
 */
export const LiveCarouselCard: React.FC<LiveCarouselCardProps> = ({
  item,
  busy,
  hasDraftEdit = false,
  readOnly = false,
  onEditInDraft,
  onToggleActive,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: busy || readOnly });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? { position: "relative", zIndex: 9999, willChange: "transform" }
      : {}),
  };

  const dateOpts: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  const expiryDate = new Date(item.expiresAt.toMillis()).toLocaleDateString(
    undefined,
    dateOpts,
  );
  const startsAtMs = item.startsAt?.toMillis();
  const isScheduled = typeof startsAtMs === "number" && startsAtMs > Date.now();
  const startDate =
    typeof startsAtMs === "number"
      ? new Date(startsAtMs).toLocaleDateString(undefined, dateOpts)
      : null;
  const isExpired = item.expiresAt.toMillis() < Date.now();

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={[
        "overflow-hidden rounded-[1.35rem] border-2 transition-all",
        isDragging
          ? "scale-[1.01] border-[#1CB0F6] bg-white opacity-90 shadow-[0_8px_0_#1899D6]"
          : isExpired
            ? "border-[#E5E5E5] bg-[#F7F7F7] opacity-60 shadow-[0_4px_0_#E0E0E0]"
            : "border-[#E5E5E5] bg-white shadow-[0_6px_0_#D0D0D0]",
      ].join(" ")}
    >
      <div className="flex items-stretch">
        <div className="flex flex-1 items-center gap-3 p-3 sm:p-4">
          {!readOnly && (
            <button
              type="button"
              {...attributes}
              {...listeners}
              disabled={busy}
              aria-label="Drag to reorder"
              title="Drag to reorder"
              className={[
                "flex h-11 w-8 cursor-grab touch-none items-center justify-center rounded-2xl border-2",
                "border-[#E5E5E5] bg-[#F7F7F7] text-[#AFAFAF]",
                "shadow-[0_3px_0_#D0D0D0] transition-all",
                "hover:text-[#4B4B4B] active:translate-y-0.5 active:cursor-grabbing active:shadow-none",
                "disabled:cursor-not-allowed disabled:opacity-50",
              ].join(" ")}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}

          <ImageCard item={item} muted={isExpired} />

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <p className="truncate text-base font-black leading-tight text-[#3C3C3C]">
                {item.title}
              </p>

              {hasDraftEdit && (
                <span className="hidden shrink-0 rounded-full border-2 border-[#FF9600] bg-[#FFE7B8] px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-[#A45A00] shadow-[0_2px_0_#E07B00] sm:inline-flex">
                  Draft pending
                </span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className={[
                  "inline-flex items-center gap-1 rounded-full border-2 px-2 py-1 text-[10px] font-black uppercase tracking-wide",
                  item.active ? statusStyles.active : statusStyles.paused,
                ].join(" ")}
              >
                <Radio className="h-3 w-3" aria-hidden />
                {item.active ? "Active" : "Paused"}
              </span>

              <div className="flex items-center gap-2">
                {item.platforms?.includes("android") && (
                  <FcAndroidOs className="h-6 w-6 text-[#000000]" />
                )}
                {item.platforms?.includes("ios") && (
                  <FaApple className="h-6 w-6 text-[#000000]" />
                )}
              </div>

              {isScheduled && (
                <span className="inline-flex items-center gap-1 rounded-full border-2 border-[#8B5CF6] bg-[#EDE9FE] px-2 py-1 text-[10px] font-black uppercase tracking-wide text-[#6D28D9] shadow-[0_2px_0_#7C3AED]">
                  <CalendarClock className="h-3 w-3" aria-hidden />
                  Scheduled {startDate}
                </span>
              )}

              <span
                className="rounded-full border-2 border-[#FF4B4B] bg-[#FFE0E0] px-2 py-1 text-[10px] font-black uppercase tracking-wide text-[#B32626] shadow-[0_2px_0_#D93636] "
                title={
                  item.expiresAt
                    ? new Date(item.expiresAt.toMillis()).toLocaleString()
                    : undefined
                }
              >
                {isExpired ? `Expired: ${expiryDate}` : "Expires " + expiryDate}
              </span>

              {hasDraftEdit && (
                <span className="inline-flex rounded-full border-2 border-[#FF9600] bg-[#FFE7B8] px-2 py-1 text-[10px] font-black uppercase tracking-wide text-[#A45A00] shadow-[0_2px_0_#E07B00] sm:hidden">
                  Draft pending
                </span>
              )}

              <ClickBadge clickCount={item.clickCount} compact />
            </div>
          </div>

          {!readOnly && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => onEditInDraft(item)}
                disabled={busy}
                aria-label="Edit in draft"
                title="Edit in draft"
                className={`${actionButtonBase} ${editButton}`}
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </button>

              <button
                type="button"
                onClick={() => onToggleActive(item)}
                disabled={busy}
                aria-label={item.active ? "Pause" : "Resume"}
                title={item.active ? "Pause" : "Resume"}
                className={`${actionButtonBase} ${
                  item.active ? pauseButton : resumeButton
                }`}
              >
                {item.active ? (
                  <Pause className="h-4 w-4" aria-hidden />
                ) : (
                  <Play className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

interface ImageCardProps {
  item: HappeningNowListItem;
  /** Desaturate the thumbnail — used to de-emphasize expired live cards. */
  muted?: boolean;
}

export const ImageCard: React.FC<ImageCardProps> = ({
  item,
  muted = false,
}) => {
  return (
    <div className="h-16 w-24 overflow-hidden rounded-2xl border-2 border-[#E5E5E5] bg-[#F7F7F7] shadow-[0_3px_0_#D0D0D0]">
      <img
        src={item.imageUrl}
        alt=""
        className={`h-full w-full object-cover ${muted ? "grayscale" : ""}`}
      />
    </div>
  );
};
